<template>
    <video ref="videoPlayerElement" class="video-js vjs-default-skin vjs-16-9" />
</template>

<script setup>

    import { onMounted, onUnmounted, ref } from 'vue';
    import videojs from 'video.js';
    import { PLAYER_COMMUNICATION } from '../../enums/player-communication.js';
    import { EventBus } from '../../utils/eventBus.js';

    const goalBufferLength = 15;
    const maxGoalBufferLength = 30;

    const props = defineProps({
        communication: {
            type: String,
            default: PLAYER_COMMUNICATION.WEBSOCKET,
        },
    });

    const videoPlayerElement = ref(null);
    const player = ref(null);

    onMounted(() => {
        player.value = createPlayer();

        switch (props.communication) {
            case PLAYER_COMMUNICATION.WEBSOCKET:
                throw new Error('Not supported yet');
            case PLAYER_COMMUNICATION.INLINE:
                setupInlineEvents();
                break;
            case PLAYER_COMMUNICATION.IPC:
                throw new Error('Not supported yet');
        }
    });

    function createPlayer() {
        videojs.Vhs.GOAL_BUFFER_LENGTH = goalBufferLength;
        videojs.Vhs.MAX_GOAL_BUFFER_LENGTH = maxGoalBufferLength;

        const options = {
            autoplay: true,
            loop: false,

            volume: 100,

            width: '100%',
            height: '30px',

            inactivityTimeout: 0,
            replay: false,

            errorDisplay: false,
            bigPlayButton: false,

            preload: true,

            controls: false,
            controlBar: false,
            userActions: {
                hotkeys: false,
            },

            sources: [],
        };

        const localPlayer = videojs(videoPlayerElement.value, options);

        localPlayer.on('timeupdate', () => {
            changeDuration(
                Math.round(localPlayer.currentTime()),
                Math.ceil(localPlayer.duration()),
            );
        });

        return localPlayer;
    }

    function blockStartedEvent(event) {
        const parentBlock = event.block.parent_block || event.block;

        if (!parentBlock?.render_url) {
            console.log('Trying to start video without render url');
            return;
        }

        console.log('Video started event: ' + parentBlock.render_url);

        player.value.currentTime(0);
        player.value.src({
            src: parentBlock.render_url,
            type: 'video/mp4',
        });

        changeDuration(0, Math.ceil(player.value.duration()));
    }

    function changeDuration(duration, maxDuration) {
        emitAction('BlockDurationChanged', {
            duration: duration,
            max_duration: maxDuration,
        });
    }

    function blockStateChangedEvent(event) {
        console.log('Video state changed event: ' + event.state);

        switch (event.state) {
            case 'duration':
                player.value.currentTime(event.duration);
                player.value.play();
                break;
            case 'stopped':
                player.value.reset();
                break;
            case 'paused':
                player.value.pause();
                break;
            case 'playing':
                player.value.play();
                break;
        }
    }

    function emitAction(action, data) {
        if (props.communication === PLAYER_COMMUNICATION.INLINE) {
            EventBus.$emit(action, data);
        } else {
            throw new Error('Not yet implemented');
        }
    }

    function setupInlineEvents() {
        EventBus.$on('BlockStarted', blockStartedEvent);
        EventBus.$on('BlockStateChanged', blockStateChangedEvent);
    }

    function removeInlineEvents() {
        EventBus.$off('BlockStarted', blockStartedEvent);
        EventBus.$off('BlockStateChanged', blockStateChangedEvent);
    }

    onUnmounted(() => {
        if (player.value) {
            player.value.dispose();
        }

        removeInlineEvents();
    });
</script>
